/**
 * Analyse mode API — Track C4
 *
 * The analysis backend is authenticated with the Firebase ID token directly
 * (same as every other backend in this app). All functions inject it via
 * analysisFetch, which mirrors authenticatedFetch from the React app.
 *
 * Base URL: NEXT_PUBLIC_ANALYSE_ENGINE_API_BASE_URL
 */

import { getIdToken } from "@/platform/firebase/auth";
import type {
  UploadProgress,
  UploadResult,
  JobStatusResponse,
  AnalysisJob,
  BackendAnalysisResponse,
} from "./types";
import { normaliseJobStatus as _normalise } from "./types";

const API_BASE =
  process.env.NEXT_PUBLIC_ANALYSE_ENGINE_API_BASE_URL || "http://localhost:8000";

// ─── Auth fetch ───────────────────────────────────────────────────────────────

/**
 * Fetch wrapper that injects the Firebase ID token as a Bearer token.
 * Mirrors `authenticatedFetch` from the React app.
 */
export async function analysisFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getIdToken();
  const headers = new Headers(options.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(url, { ...options, headers });
}

// ─── GCS Upload (3-step) ──────────────────────────────────────────────────────

function getContentType(filename: string): string {
  const ext = filename.toLowerCase().split(".").pop();
  switch (ext) {
    case "csv":
    case "txt":
      return "text/csv";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "xls":
      return "application/vnd.ms-excel";
    default:
      return "application/octet-stream";
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
}

function uploadToGCSWithProgress(
  uploadUrl: string,
  file: File,
  onProgress: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress((e.loaded / e.total) * 100);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`GCS upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("GCS upload network error")));
    xhr.addEventListener("abort", () => reject(new Error("GCS upload aborted")));

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type || getContentType(file.name));
    xhr.send(file);
  });
}

/**
 * Upload a file to GCS via the 3-step signed URL flow.
 * Step 1: Generate signed URL  →  Step 2: PUT to GCS  →  Step 3: Process on backend
 */
export async function uploadFile(
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  onProgress?.({ percent: 5, phase: "generating_url", message: "Preparing upload..." });

  // Step 1: Get signed upload URL
  const urlResponse = await analysisFetch(
    `${API_BASE}/api/gcs/generate-upload-url`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type || getContentType(file.name),
      }),
    }
  );

  if (!urlResponse.ok) {
    const err = await urlResponse.json().catch(() => ({}));
    throw new Error((err as any).detail || "Failed to generate upload URL");
  }

  const { upload_url, gcs_key, file_id } = await urlResponse.json();

  onProgress?.({
    percent: 10,
    phase: "uploading",
    message: `Uploading ${formatFileSize(file.size)}...`,
  });

  // Step 2: Upload directly to GCS with progress
  await uploadToGCSWithProgress(upload_url, file, (pct) => {
    onProgress?.({
      percent: 10 + pct * 0.7, // 10% → 80%
      phase: "uploading",
      message: `Uploading ${formatFileSize(file.size)}... ${Math.round(pct)}%`,
    });
  });

  onProgress?.({ percent: 85, phase: "processing", message: "Processing file..." });

  // Step 3: Notify backend
  const processResponse = await analysisFetch(
    `${API_BASE}/api/gcs/process-gcs-file`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ gcs_key, file_id, filename: file.name }),
    }
  );

  if (!processResponse.ok) {
    const err = await processResponse.json().catch(() => ({}));
    throw new Error((err as any).detail || "Failed to process uploaded file");
  }

  const result = await processResponse.json();
  onProgress?.({ percent: 100, phase: "complete", message: "Upload complete!" });
  return result as UploadResult;
}

// ─── Job submission & polling ─────────────────────────────────────────────────

/**
 * Submit a file for analysis. Returns { job_id, status }.
 * If status === 'CACHED', redirect to job detail immediately.
 */
export async function submitAnalysisJob(
  fileId: string,
  userId: string
): Promise<{ job_id: string; status: string }> {
  const formData = new FormData();
  formData.append("file_id", fileId);
  formData.append("tenant_id", userId);
  formData.append("user_id", userId);

  const response = await analysisFetch(
    `${API_BASE}/api/run-analysis`,
    { method: "POST", body: formData }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).detail || `Failed to submit analysis: ${response.status}`);
  }

  return response.json();
}

export async function getJobStatus(
  jobId: string,
  signal?: AbortSignal
): Promise<JobStatusResponse> {
  const response = await analysisFetch(
    `${API_BASE}/api/check-analysis-status/${jobId}`,
    { signal }
  );
  if (!response.ok) throw new Error(`Status check failed: ${response.status}`);
  return response.json();
}

// ─── Job CRUD ─────────────────────────────────────────────────────────────────

export async function listJobs(
  limit = 20
): Promise<AnalysisJob[]> {
  const response = await analysisFetch(
    `${API_BASE}/api/jobs?limit=${limit}`,
    {}
  );
  if (!response.ok) throw new Error(`Failed to list jobs: ${response.status}`);
  const data = await response.json();

  return (data.jobs as any[])
    .map((job) => ({
      id: job.id,
      title: job.job_title || "Untitled Analysis",
      type: job.analysis_type || "Analysis",
      status: _normalise(job.status),
      createdAt: new Date(job.created_at).toLocaleString(),
      timestamp: new Date(job.created_at).getTime(),
      results: null,
      summary: undefined,
      progress: job.progress || 0,
      progressMessage: job.current_phase || "",
    }))
    .sort((a: AnalysisJob, b: AnalysisJob) => b.timestamp - a.timestamp);
}

export async function deleteJob(
  jobId: string
): Promise<void> {
  const response = await analysisFetch(
    `${API_BASE}/api/jobs/${jobId}`,
    { method: "DELETE" }
  );
  if (!response.ok) throw new Error(`Failed to delete job: ${response.status}`);
}

export async function renameJob(
  jobId: string,
  title: string
): Promise<void> {
  const formData = new FormData();
  formData.append("title", title.trim());

  const response = await analysisFetch(
    `${API_BASE}/api/jobs/${jobId}/title`,
    { method: "PATCH", body: formData }
  );
  if (!response.ok) throw new Error(`Failed to rename job: ${response.status}`);
}

export async function getAnalysis(
  jobId: string
): Promise<BackendAnalysisResponse & { title?: string; type?: string; created_at?: string }> {
  const response = await analysisFetch(
    `${API_BASE}/api/analyses/${jobId}`,
    {}
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as any).detail || `Failed to load analysis: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Download Excel export for a completed analysis.
 * Triggers browser download directly.
 */
export async function downloadExcel(
  analysisId: string
): Promise<void> {
  const response = await analysisFetch(
    `${API_BASE}/api/analysis/${analysisId}/download-excel`,
    {}
  );
  if (!response.ok) throw new Error("Failed to download Excel");

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `analysis-${analysisId}.xlsx`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Download PDF report for a completed analysis.
 * Hits the backend PDF generation endpoint and triggers browser download.
 */
export async function downloadPdf(
  analysisId: string
): Promise<void> {
  const response = await analysisFetch(
    `${API_BASE}/api/analysis/${analysisId}/download-pdf`,
    {}
  );
  if (!response.ok) throw new Error("Failed to download PDF");

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `analysis-${analysisId}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
